import { useEffect, ReactNode, useMemo, useContext, useState } from "react";
import { GrowthBook, configureCache } from "@growthbook/growthbook";
import { GrowthBookProvider } from "@growthbook/growthbook-react";
import { autoAttributesPlugin } from "@growthbook/growthbook/plugins";
import RbsContext from "src/utils/RoleBasedSecurity/RbsContext";
import { getSession } from "src/utils/RoleBasedSecurity/Session";
import jwt_decode from "jwt-decode";

interface IFeatureFlagsProvider {
  children: ReactNode;
  // TODO: these should be moved to .env, when possible
  clientKey: string;
  enableDevMode: boolean;
}

declare global {
  interface Window {
    growthbookInstance: GrowthBook | null;
  }
}

window.growthbookInstance = null;

interface Token {
  realm_access: { roles: string[] };
}

const FeatureFlagsProvider = ({
  children,
  clientKey,
  enableDevMode,
}: IFeatureFlagsProvider) => {
  const { userInfo } = useContext(RbsContext);
  const { isAhr, getId } = userInfo;

  const [hasHrToolsEarlyAccess, setHasHrToolsEarlyAccess] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const session = getSession();
        if (session) {
          const decodedToken: Token = jwt_decode(
            await session.getValidAccessToken()
          );
          const hasAccess = decodedToken.realm_access.roles.includes(
            "hr_tools_early_access"
          );
          setHasHrToolsEarlyAccess(hasAccess);
        }
      } catch (error) {
        setHasHrToolsEarlyAccess(false);
      }
    })();
  }, []);

  const GB = useMemo(
    () =>
      new GrowthBook({
        apiHost: "https://api.mybenefit.pl",
        enableDevMode,
        clientKey,
        plugins: [autoAttributesPlugin()],
      }),
    [clientKey, enableDevMode]
  );

  useEffect(() => {
    const userId = getId();
    const isUserAhr = isAhr();

    if (
      !userId ||
      isUserAhr === undefined ||
      hasHrToolsEarlyAccess === undefined
    ) {
      return;
    }

    configureCache({
      disableCache: true,
    });

    GB.init({ streaming: true });
    GB.setAttributes({
      userId,
      isAhr: isUserAhr,
      hasHrToolsEarlyAccess,
    });

    window.growthbookInstance = GB;
  }, [GB, getId, hasHrToolsEarlyAccess, isAhr]);

  return <GrowthBookProvider growthbook={GB}>{children}</GrowthBookProvider>;
};

export default FeatureFlagsProvider;
